import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "", email: "", password: "", phone: "",
        customer_type: "B2C", company_name: "", gst_number: "",
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await register(form);
        setLoading(false);
        if (!res.ok) {
            setErr(res.error);
            toast.error(res.error);
            return;
        }
        toast.success("Account created!");
        navigate("/my-orders");
    };

    return (
        <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center py-16 px-4" data-testid="register-page">
            <div className="w-full max-w-xl">
                <div className="bg-white border border-slate-200 rounded-sm p-10">
                    <div className="mb-8">
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-3">// Create Account</div>
                        <h1 className="font-heading font-black text-3xl tracking-tighter text-slate-900">Get on the delivery grid.</h1>
                        <p className="mt-2 text-sm text-slate-600">Track orders. Reorder in seconds. Faster B2B invoicing.</p>
                    </div>

                    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full Name*">
                            <Input required value={form.name} onChange={e => set("name", e.target.value)} className="rounded-sm" data-testid="register-name-input" />
                        </Field>
                        <Field label="Phone*">
                            <Input required value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91 ..." className="rounded-sm" data-testid="register-phone-input" />
                        </Field>
                        <Field label="Email*" className="sm:col-span-2">
                            <Input type="email" required value={form.email} onChange={e => set("email", e.target.value)} className="rounded-sm" data-testid="register-email-input" />
                        </Field>
                        <Field label="Password* (min 6)">
                            <Input type="password" required minLength={6} value={form.password} onChange={e => set("password", e.target.value)} className="rounded-sm" data-testid="register-password-input" />
                        </Field>
                        <Field label="Customer Type*">
                            <Select value={form.customer_type} onValueChange={v => set("customer_type", v)}>
                                <SelectTrigger data-testid="register-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="B2C">Individual (B2C)</SelectItem>
                                    <SelectItem value="B2B">Business (B2B)</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        {form.customer_type === "B2B" && (
                            <>
                                <Field label="Company Name">
                                    <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} className="rounded-sm" data-testid="register-company-input" />
                                </Field>
                                <Field label="GST Number">
                                    <Input value={form.gst_number} onChange={e => set("gst_number", e.target.value)} className="rounded-sm" data-testid="register-gst-input" />
                                </Field>
                            </>
                        )}
                        {err && <div className="sm:col-span-2 text-sm text-red-600" data-testid="register-error">{err}</div>}
                        <Button type="submit" disabled={loading} className="sm:col-span-2 h-11 rounded-sm bg-blue-700 hover:bg-blue-800 font-bold uppercase tracking-wider" data-testid="register-submit-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>}
                        </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 text-center">
                        Already have an account? <Link to="/login" className="text-blue-700 font-bold hover:underline" data-testid="register-login-link">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
